import { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { ChartLineUp } from "@phosphor-icons/react";

const COLORS = ["#00C805", "#FF9500", "#5AC8FA", "#FF3B30", "#BF5AF2"];

export default function BotCompareModal({ open, onOpenChange, botIds }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || botIds.length === 0) return;
    setLoading(true);
    api
      .post("/bots/compare", { bot_ids: botIds })
      .then((r) => setSeries(r.data.series || []))
      .finally(() => setLoading(false));
  }, [open, botIds]);

  // Merge each bot's points + buy-hold points into a single frame keyed by time
  const chartData = (() => {
    const bag = {};
    series.forEach((s) => {
      const symbolKey = series.filter((x) => x.symbol === s.symbol).length > 1
        ? `${s.symbol} #${s.bot_id.slice(-4)}`
        : s.symbol;
      s.points.forEach((p) => {
        if (!bag[p.t]) bag[p.t] = { t: p.t };
        bag[p.t][symbolKey] = p.cum_pl;
      });
      (s.buy_hold_points || []).forEach((p) => {
        if (!bag[p.t]) bag[p.t] = { t: p.t };
        bag[p.t][`${symbolKey} · buy&hold`] = p.bh_pl;
      });
    });
    return Object.values(bag).sort((a, b) => new Date(a.t) - new Date(b.t));
  })();

  // Line specs so we can style buy-hold as dashed
  const lineSpecs = (() => {
    const specs = [];
    series.forEach((s, i) => {
      const symbolKey = series.filter((x) => x.symbol === s.symbol).length > 1
        ? `${s.symbol} #${s.bot_id.slice(-4)}`
        : s.symbol;
      const color = COLORS[i % COLORS.length];
      specs.push({ key: symbolKey, color, dashed: false });
      if ((s.buy_hold_points || []).length > 0) {
        specs.push({ key: `${symbolKey} · buy&hold`, color, dashed: true });
      }
    });
    return specs;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="bot-compare-modal"
        className="bg-zinc-900 border-zinc-800 rounded-sm max-w-4xl"
      >
        <DialogHeader>
          <DialogTitle className="heading text-2xl tracking-tighter flex items-center gap-2">
            <ChartLineUp size={22} className="text-[#00C805]" /> Bot comparison
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Cumulative realized P/L from each bot's first trade, with a dashed
            <span className="text-zinc-400"> buy &amp; hold</span> line for the same symbol +
            quantity — see whether your strategy actually beats doing nothing.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center mono text-xs text-zinc-500">Crunching trades…</div>
        ) : series.length === 0 ? (
          <div className="py-16 text-center mono text-xs text-zinc-600">
            No comparable bots selected.
          </div>
        ) : (
          <>
            {/* Stats table */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {series.map((s, i) => {
                const bh = s.stats.buy_hold_pl ?? 0;
                const delta = s.stats.beats_buy_hold ?? (s.stats.total_realized_pl - bh);
                const wonVsBh = delta > 0;
                return (
                  <div
                    key={s.bot_id}
                    data-testid={`compare-stat-${s.symbol}`}
                    className="p-4 border border-zinc-800 rounded-sm bg-zinc-950"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: COLORS[i % COLORS.length] }}
                      />
                      <span className="font-semibold text-base">{s.symbol}</span>
                      <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {s.strategy}
                      </span>
                    </div>
                    <div
                      className={`mono text-2xl font-bold mt-2 ${
                        s.stats.total_realized_pl >= 0 ? "profit" : "loss"
                      }`}
                    >
                      {fmtMoney(s.stats.total_realized_pl, { showSign: true })}
                    </div>
                    <div className="mono text-[10px] text-zinc-500 mt-1 space-x-2">
                      <span>{s.stats.trades} trades</span>
                      <span>·</span>
                      <span>
                        {s.stats.sells > 0 ? fmtPct(s.stats.win_rate) : "—"} win rate
                      </span>
                    </div>
                    {(s.buy_hold_points || []).length > 0 && (
                      <div
                        data-testid={`compare-vs-bh-${s.symbol}`}
                        className={`mono text-[10px] mt-2 pt-2 border-t border-zinc-800 uppercase tracking-widest ${
                          wonVsBh ? "profit" : "loss"
                        }`}
                      >
                        {wonVsBh ? "▲ Beats" : "▼ Loses to"} buy &amp; hold by{" "}
                        {fmtMoney(Math.abs(delta), { showSign: false })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Chart */}
            <div className="h-72 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="t"
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    tickFormatter={(t) =>
                      new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                    }
                  />
                  <YAxis
                    tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }}
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#18181b",
                      border: "1px solid #3f3f46",
                      borderRadius: 2,
                      fontFamily: "monospace",
                      fontSize: 12,
                    }}
                    formatter={(v) => fmtMoney(v, { showSign: true })}
                    labelFormatter={(t) => new Date(t).toLocaleString()}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                  {lineSpecs.map((spec) => (
                    <Line
                      key={spec.key}
                      type="monotone"
                      dataKey={spec.key}
                      stroke={spec.color}
                      strokeWidth={spec.dashed ? 1.5 : 2}
                      strokeDasharray={spec.dashed ? "5 4" : undefined}
                      strokeOpacity={spec.dashed ? 0.55 : 1}
                      dot={false}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
